import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  AppConfig,
  ApplicantModel,
  DataApiResponse,
  DigitalVacancyListItemModel,
  DigitalVacancyModel,
  EditUserWorkHistoryListModel,
  EducationLevelModel,
  ProfessionAliasModel,
  SearchMode,
  UserWorkHistoryModel,
  VacancyApplyResultModel,
  VacancyProfessionsInfoModel,
} from '@digilize/shared/definitions/src';
import { SKIP_ERROR_INTERCEPTOR } from '@digilize/shared/feature/modules/auth';
import { APP_CONFIG } from '@digilize/shared/utils/tokens';
import * as moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class VacaniesService {
  private readonly http = inject(HttpClient);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);

  setVacancyId(id: string) {
    const vacancyIdExpiration = moment().add(1, 'minutes');

    localStorage.setItem(this.appConfig.localStorageAuthPrefix + 'vacancyId', id);
    localStorage.setItem(
      this.appConfig.localStorageAuthPrefix + 'vacancyIdExpiration',
      JSON.stringify(vacancyIdExpiration.valueOf())
    );
  }

  checkVacancyExpiration() {
    const vacancyIdExpiration = localStorage.getItem('bwd-vacancyIdExpiration');
    if (Number(vacancyIdExpiration) > moment.now()) {
      return true;
    }
    return false;
  }

  getVacancyDetails = (vacancyId: string, searchMode: SearchMode) =>
    this.http.get<DigitalVacancyModel>(`/api/v1.0/vacancy/${vacancyId}`, {
      params: { purpose: 1, search_mode: searchMode },
    });

  vacancyApply = (vacancyId: string, req: unknown) =>
    this.http.post<VacancyApplyResultModel>(`/api/v1.0/vacancy/${vacancyId}/apply`, req);

  vacancyApplied = (vacancyId: string, req: unknown) => this.http.post(`/api/v1.0/vacancy/${vacancyId}/applied`, req);

  vacancySearch = (params: Record<string, unknown>) =>
    this.http.get<DataApiResponse<DigitalVacancyListItemModel[]>>('/api/v1.0/vacancy/search', {
      params: params as Record<string, string | number | boolean>,
    });

  getMyVacancies = (params: Record<string, unknown>) =>
    this.http.get<DataApiResponse<DigitalVacancyModel[]>>('/api/v1.0/my/vacancy', {
      params: params as Record<string, string | number | boolean>,
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
    });

  getMyFavoriteVacancies = (params: Record<string, unknown> = {}) =>
    this.http.get<DataApiResponse<DigitalVacancyModel[]>>('/api/v1.0/my/vacancy/favorites', {
      params: params as Record<string, string | number | boolean>,
    });

  toggleFavoriteVacancy = (id: number | string) => this.http.post<void>(`/api/v1.0/my/vacancy/${id}/favorite`, {});

  getVacanciesByCompany = (params: Record<string, unknown>) =>
    this.http.get<DataApiResponse<DigitalVacancyListItemModel[]>>('/api/v1.0/vacancy/bycompany/', {
      params: params as Record<string, string | number | boolean>,
    });

  getVacancyApplicants = (params: Record<string, unknown>) =>
    this.http.get<DataApiResponse<ApplicantModel[]>>('/api/v1.0/applicants', {
      params: params as Record<string, string | number | boolean>,
    });

  getVacancyProfessionsData = (vacancyId: string) =>
    this.http.get<VacancyProfessionsInfoModel>(`/api/v1.0/vacancy/${vacancyId}/professions`);

  putVacancyProfessions = (req: VacancyProfessionsInfoModel) =>
    this.http.put<VacancyProfessionsInfoModel>('/api/v1.0/vacancy/professions/', req);

  getManualVacancy = (req: unknown) => this.http.post('/api/v1.0/manualvacancy', req);

  getListOfProfessions = (searchValue: string) =>
    this.http.get<DataApiResponse<ProfessionAliasModel[]>>('/api/v1.0/professions/', {
      params: {
        'search.value': searchValue,
        length: 10,
      },
    });

  uploadVacancy = (vacancy: unknown) => this.http.post<DigitalVacancyModel>('/api/v1.0/vacancy', vacancy);

  getProfessionsMy = () => this.http.get<DataApiResponse<UserWorkHistoryModel[]>>('/api/v1.0/my/professions');

  putProfessionsMy = (req: EditUserWorkHistoryListModel) =>
    this.http.put<DataApiResponse<UserWorkHistoryModel[]>>('/api/v1.0/my/professions', req);

  getLevels = (searchValue: string, code: string) =>
    this.http.get<DataApiResponse<EducationLevelModel[]>>('/api/v1.0/education/levels', {
      params: {
        'search.value': searchValue,
        country: code,
        length: 115,
      },
    });
}
